<template>
  <div class="community-map">
    <div v-if="!pins.length" class="map-empty iabf-card">
      <p>
        No mappable locations yet. When dancers add
        <strong>Where are you coming from?</strong> on their intro
        (e.g. “Austin, TX” or “Berlin, Germany”), they appear here.
      </p>
    </div>

    <div v-show="pins.length" ref="mapEl" class="map-canvas" role="img" aria-label="World map of dancer home regions" />

    <ul v-if="pins.length" class="map-legend" aria-label="People on the map">
      <li v-for="pin in pins" :key="pin.id">
        <button type="button" class="legend-btn" @click="focusPin(pin)">
          <span class="legend-dot" />
          <span class="legend-name">{{ pin.name }}</span>
          <span class="legend-from">{{ pin.comingFrom }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { pinsFromIntros } from '@/utils/iabf-community-geo'

// Free Carto basemap key (tile URL is public; domain-limited by Carto).
const CARTO_BASEMAP_KEY = 'cb1_2utr_1_dfd01c71c0b376d08ef8dcd3'

export default {
  name: 'CommunityMap',
  props: {
    intros: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      map: null,
      layer: null,
      markerById: {}
    }
  },
  computed: {
    pins () {
      return pinsFromIntros(this.intros)
    }
  },
  watch: {
    pins: {
      deep: true,
      handler () {
        this.$nextTick(() => this.renderPins())
      }
    }
  },
  mounted () {
    this.initMap()
    this.renderPins()
  },
  beforeDestroy () {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    initMap () {
      if (this.map || !this.$refs.mapEl) {
        return
      }
      this.map = L.map(this.$refs.mapEl, {
        worldCopyJump: true,
        minZoom: 1,
        maxZoom: 8,
        zoomControl: true,
        attributionControl: true
      }).setView([20, 0], 2)

      // Carto Voyager raster tiles require ?key= (watermark without it).
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=' + CARTO_BASEMAP_KEY, {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 18,
        noWrap: false
      }).addTo(this.map)

      this.layer = L.layerGroup().addTo(this.map)

      // Sage festival pin
      this.pinIcon = L.divIcon({
        className: 'iabf-map-pin',
        html: '<span class="iabf-map-pin-dot"></span>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
        popupAnchor: [0, -10]
      })

      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize()
        }
      }, 80)
    },
    renderPins () {
      if (!this.map) {
        this.initMap()
      }
      if (!this.map || !this.layer) {
        return
      }
      this.layer.clearLayers()
      this.markerById = {}
      const pins = this.pins
      if (!pins.length) {
        this.map.setView([20, 0], 2)
        return
      }
      const bounds = []
      pins.forEach(pin => {
        const m = L.marker([pin.lat, pin.lng], { icon: this.pinIcon })
        const safeName = this.escapeHtml(pin.name)
        const safeFrom = this.escapeHtml(pin.comingFrom)
        const faculty = pin.isFaculty ? '<span class="iabf-map-faculty">Faculty</span>' : ''
        const photo = pin.headshotUrl
          ? `<img class="iabf-map-popup-photo" src="${this.escapeHtml(pin.headshotUrl)}" alt="">`
          : `<span class="iabf-map-popup-photo is-empty">${this.escapeHtml(this.initials(pin.name))}</span>`
        m.bindPopup(
          `<div class="iabf-map-popup">${photo}<div class="iabf-map-popup-text"><strong>${safeName}</strong>${faculty}<span>${safeFrom}</span></div></div>`,
          { maxWidth: 280, minWidth: 180 }
        )
        m.addTo(this.layer)
        this.markerById[pin.id] = m
        bounds.push([pin.lat, pin.lng])
      })
      try {
        this.map.fitBounds(bounds, { padding: [36, 36], maxZoom: 5 })
      } catch (e) {
        this.map.setView([20, 0], 2)
      }
      this.$nextTick(() => {
        if (this.map) {
          this.map.invalidateSize()
        }
      })
    },
    focusPin (pin) {
      if (!this.map || !pin) {
        return
      }
      this.map.setView([pin.lat, pin.lng], Math.max(this.map.getZoom(), 4), { animate: true })
      const m = this.markerById[pin.id]
      if (m) {
        m.openPopup()
      }
    },
    initials (name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) {
        return '?'
      }
      const a = parts[0].charAt(0)
      const b = parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''
      return (a + b).toUpperCase()
    },
    escapeHtml (s) {
      return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    }
  }
}
</script>

<style scoped>
.community-map {
  margin-top: 8px;
}
.map-empty {
  padding: 20px 18px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
}
.map-canvas {
  height: min(62vh, 480px);
  min-height: 320px;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  background: #dfe8e4;
  z-index: 0;
}
.map-legend {
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
}
.legend-btn {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  width: 100%;
  text-align: left;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  background: #fff;
  border-radius: 12px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.legend-btn:hover {
  border-color: var(--iabf-primary, #8a957d);
  background: rgba(138, 149, 125, 0.08);
}
.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--iabf-primary, #8a957d);
  flex-shrink: 0;
  margin-top: 5px;
}
.legend-name {
  font-weight: 700;
  font-size: 14px;
  color: var(--iabf-ink, #1c1b1f);
}
.legend-from {
  flex: 1 1 100%;
  padding-left: 16px;
  font-size: 13px;
  color: var(--iabf-ink-muted, #5c5a63);
}
</style>

<!-- Unscoped: Leaflet creates popup DOM outside scoped root -->
<style>
.iabf-map-pin {
  background: transparent !important;
  border: none !important;
}
.iabf-map-pin-dot {
  display: block;
  width: 14px;
  height: 14px;
  margin: 2px;
  border-radius: 50%;
  background: #8a957d;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(28, 27, 31, 0.35);
}
.iabf-map-popup {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.35;
  color: #1c1b1f;
  min-width: 0;
}
.iabf-map-popup-photo {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #eef0eb;
}
.iabf-map-popup-photo.is-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #8a8792;
}
.iabf-map-popup-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.iabf-map-popup strong {
  font-weight: 700;
}
.iabf-map-popup-text span {
  color: #5c5a63;
}
.community-map .leaflet-container {
  font-family: inherit;
  background: #dfe8e4;
}
</style>
